import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Alert,
  AlertDescription,
  BadgeAlt as Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@cargomatic/ui';
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Info,
  Loader2,
  Paperclip,
  Plus,
  Trash2,
} from 'lucide-react';
import { startCase } from 'lodash';
import { updateLaneAccessorialRates } from 'utils/AccessorialRequestWebAPIUtils';
import { cn } from 'utils/ui/classes';
import { CargomaticDropzone } from 'components/v2/Dropzone';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import {
  ACCESSORIAL_RATE_FIELDS,
  ACTION,
  BLANK_ACCESSORIAL_RATE,
  LEG_TYPES,
  STATUS,
} from 'constants/AccessorialRates';
import { ACCEPTED_FILE_TYPES } from 'config/accepted-file-types';
import { showToast } from 'stores/ToastStore';
import AppointmentWindowModal from './LaneRatesAppointmentWindowModal';
import {
  createLaneAccessorialRatesPayload,
  fetchAccessorials,
  fetchExistingAccessorialRates,
  fetchStandardAccessorialRates,
} from './helpers/accessorial-rate-helpers';

const FORMATS = [
  { name: '$', value: '$', localizationSlug: 'DOLLAR' },
  {
    name: '% of line haul rate',
    value: '%',
    localizationSlug: 'PERCENT_OF_LANE_HAUL_RATE',
  },
];

const UNITS = [
  { name: 'Per Event', value: 'per event', localizationSlug: 'PER_EVENT' },
  { name: 'Per Day', value: 'per day', localizationSlug: 'PER_DAY' },
];

const DATE_TIME_FORMAT = 'L';

const ERROR_FIELD_CLASS =
  'border-red-500 focus:ring-red-500 focus-visible:ring-red-500';

const isDateRangeValid = (start, end) => Boolean(start && end && end > start);

const getDuplicateAccessorialIndexes = (rates) => {
  const firstSeen = new Map();
  const duplicates = new Set();
  rates.forEach((rate, index) => {
    const type = rate.accessorialtype;
    if (!type) return;
    if (firstSeen.has(type)) {
      duplicates.add(index);
      duplicates.add(firstSeen.get(type));
    } else {
      firstSeen.set(type, index);
    }
  });
  return duplicates;
};

const getRowErrors = (rate, isDuplicate) => ({
  accessorialtype: !rate.accessorialtype,
  ratetype: !rate.ratetype,
  rate: !(Number(rate.rate) > 0),
  units: !rate.units,
  duplicate: isDuplicate,
});

const hasAnyRowError = (errors) =>
  errors.accessorialtype ||
  errors.ratetype ||
  errors.rate ||
  errors.units ||
  errors.duplicate;

export function AddAccessorialRatesModal({
  showModal,
  onClose,
  onBack,
  handleSubmit,
  handleDropFiles,
  uploadingFiles,
  disableButtons,
  loadingConfirmation,
  customerData,
  market,
  isEdit = false,
}) {
  const [
    showAccessorialRatesAppointmentModal,
    setShowAccessorialRatesAppointmentModal,
  ] = useState({ show: false, index: null });
  const [date, setDate] = useState({
    effectiveStart: null,
    effectiveEnd: null,
  });
  const [accessorials, setAccessorials] = useState([]);
  const [newAccessorialRate, setNewAccessorialRate] = useState(
    BLANK_ACCESSORIAL_RATE,
  );
  const [laneAccessorialRates, setLaneAccessorialRates] = useState([]);
  const [deleteError, setDeleteError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFetchingAccessorials, setIsFetchingAccessorials] = useState(false);
  const [isFetchingAccessorialRates, setIsFetchingAccessorialRates] =
    useState(false);

  const [isEdited, setIsEdited] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const [laneAccessorialRatesPayload, setLaneAccessorialRatesPayload] =
    useState([]);
  const { t } = useTranslation();

  const formatDate = (d) => {
    if (!d) return t('NOT_SET');
    return moment(d).format(DATE_TIME_FORMAT);
  };

  // derived validation (pure, recomputed on state change)
  const rowErrors = useMemo(() => {
    const duplicates = getDuplicateAccessorialIndexes(laneAccessorialRates);
    return laneAccessorialRates.map((rate, index) =>
      getRowErrors(rate, duplicates.has(index)),
    );
  }, [laneAccessorialRates]);

  const duplicateAccessorial = useMemo(
    () => rowErrors.some((errors) => errors.duplicate),
    [rowErrors],
  );

  const areDatesValid = useMemo(
    () => isDateRangeValid(date.effectiveStart, date.effectiveEnd),
    [date],
  );

  const allRowsValid = useMemo(
    () => rowErrors.length > 0 && rowErrors.every((e) => !hasAnyRowError(e)),
    [rowErrors],
  );

  const doCustomersHaveFiles = useMemo(() => {
    if (!isEdited) return true;
    return customerData.every(
      (customer) => customer.files && customer.files.length,
    );
  }, [customerData, isEdited]);

  const hasRates = laneAccessorialRates.length > 0;
  const allAccessorialRatesValid =
    hasRates && areDatesValid && allRowsValid && doCustomersHaveFiles;

  // useEffects
  useEffect(() => {
    const fetchData = async () => {
      setIsFetchingAccessorials(true);
      try {
        const result = await fetchAccessorials();
        setAccessorials(result);
      } catch (_e) {
        showToast(t('REQUEST_ERROR'), t('LOADING_ACCESSORIALS_ERROR'), 'error');
      } finally {
        setIsFetchingAccessorials(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchAccessorialRatesData = async () => {
      setIsFetchingAccessorialRates(true);
      try {
        const { customer, customerType } = customerData[0];
        const existingAccessorialRates = await fetchExistingAccessorialRates({
          status: STATUS.CONFIRMED,
          market,
          customer,
        });
        let rates;
        if (existingAccessorialRates && existingAccessorialRates.length) {
          setDate({
            effectiveStart: existingAccessorialRates[0].effectiveStart,
            effectiveEnd: existingAccessorialRates[0].effectiveEnd,
          });
          rates = existingAccessorialRates;
        } else {
          rates = await fetchStandardAccessorialRates({
            customer,
            customerType,
            market,
          });
        }
        rates = rates.map((accessorialRate) => ({ ...accessorialRate }));
        setLaneAccessorialRates(rates);
        if (rates && rates.length) {
          setIsEdited(true);
        }
      } catch (_e) {
        showToast(t('REQUEST_ERROR'), t('LOADING_ACCESSORIALS_ERROR'), 'error');
      } finally {
        setIsFetchingAccessorialRates(false);
      }
    };
    fetchAccessorialRatesData();
  }, []);

  useEffect(() => {
    const payload = createLaneAccessorialRatesPayload({
      customerData,
      date,
      market,
      laneAccessorialRates,
    });
    setLaneAccessorialRatesPayload(payload);
  }, [date, customerData, market, laneAccessorialRates]);

  useEffect(() => {
    setIsLoading(
      isDeleting ||
        isFetchingAccessorials ||
        isFetchingAccessorialRates ||
        uploadingFiles ||
        loadingConfirmation,
    );
  }, [
    isDeleting,
    isFetchingAccessorials,
    isFetchingAccessorialRates,
    uploadingFiles,
    loadingConfirmation,
  ]);

  // handlers
  const checkUserType = (data = [], customerType = 'shipper') => {
    if (!data?.length) return false;
    return !!data.find((customer) => customer?.customerType === customerType);
  };

  const isAutoApplyFeatureAllowed = (data = [], customerType = 'shipper') => {
    const isFeatureEnabled = featureEnabled(
      CONFIGURED_FEATURES?.AUTO_APPLY_ACCESSORIAL,
    );
    const isValidUserType = checkUserType(data, customerType);
    return isFeatureEnabled && isValidUserType;
  };

  const handleAddNewAccessorialRate = () => {
    const currNewAccessorialRate = { ...newAccessorialRate };
    currNewAccessorialRate.effectiveEnd = date.effectiveEnd;
    currNewAccessorialRate.effectiveStart = date.effectiveStart;
    setLaneAccessorialRates((prev) => [...prev, currNewAccessorialRate]);
    const defaultAccessorialRate = Object.assign({}, BLANK_ACCESSORIAL_RATE);
    const isShipper = checkUserType(customerData);
    if (!isShipper) {
      delete defaultAccessorialRate?.autoApply;
    }
    setNewAccessorialRate(defaultAccessorialRate);
  };

  const handleNewAccessorialRateChange = (field, value) => {
    setNewAccessorialRate((prev) => ({ ...prev, [field]: value }));
  };

  const handleAccessorialRateAppointment = (index) => {
    setIsEdited(true);
    setShowAccessorialRatesAppointmentModal({ show: true, index });
  };

  const handleAccessorialRateFieldChange = (rate, index, field) => {
    setIsEdited(true);
    setLaneAccessorialRates((prev) => [
      ...prev.slice(0, index),
      { ...prev[index], [field]: rate },
      ...prev.slice(index + 1),
    ]);
  };

  const handleRemoveAccessorialRate = (index) => {
    setLaneAccessorialRates((prev) => [
      ...prev.slice(0, index),
      ...prev.slice(index + 1),
    ]);
  };

  const handleDeleteAccessorialRate = async (index) => {
    setIsDeleting(true);
    setDeleteError(false);
    const accessorialRate = laneAccessorialRates[index];
    try {
      if (accessorialRate.createdAt) {
        accessorialRate.effectiveEnd = moment()
          .add(-1, 'day')
          .endOf('day')
          .utc()
          .toISOString();
        accessorialRate.status = STATUS.EXPIRED;
        accessorialRate.alreadyConfirmed = true;
        await updateLaneAccessorialRates([accessorialRate], ACTION.REMOVE);
      }
      handleRemoveAccessorialRate(index);
      setIsDeleting(false);
    } catch (_e) {
      setIsDeleting(false);
      setDeleteError(true);
    }
  };

  const handleClickSubmit = async () => {
    if (!allAccessorialRatesValid) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    await handleSubmit(laneAccessorialRatesPayload);
  };

  const hideAccessorialRateAppointment = () => {
    setShowAccessorialRatesAppointmentModal({ show: false, index: null });
  };

  const handleOpenChange = (open) => {
    if (!open) {
      if (loadingConfirmation || uploadingFiles) return;
      onClose(true);
    }
  };

  const showCheckbox = isAutoApplyFeatureAllowed(customerData);

  // --- Render helpers ---

  const renderAccessorialRateType = ({
    accessorialRate,
    index,
    isNew = false,
    hasError = false,
  }) => {
    const onSelectTableRow = (value) => {
      const selected = accessorials.find((a) => a.name === value);
      if (selected) {
        handleAccessorialRateFieldChange(
          selected.name,
          index,
          ACCESSORIAL_RATE_FIELDS.ACCESSORIAL_TYPE,
        );
        handleAccessorialRateFieldChange(
          selected.charge_code,
          index,
          ACCESSORIAL_RATE_FIELDS.ACCESSORIAL_CODE,
        );
      }
    };

    const onSelectNewRate = (value) => {
      const selected = accessorials.find((a) => a.name === value);
      if (selected) {
        handleNewAccessorialRateChange(
          ACCESSORIAL_RATE_FIELDS.ACCESSORIAL_TYPE,
          selected.name,
        );
        handleNewAccessorialRateChange(
          ACCESSORIAL_RATE_FIELDS.ACCESSORIAL_CODE,
          selected.charge_code,
        );
      }
    };

    const onValueChange = isNew ? onSelectNewRate : onSelectTableRow;

    return (
      <Select
        value={accessorialRate.accessorialtype || ''}
        onValueChange={onValueChange}
      >
        <SelectTrigger className={cn('w-full', hasError && ERROR_FIELD_CLASS)}>
          <SelectValue placeholder={startCase(t('TYPE'))} />
        </SelectTrigger>
        <SelectContent>
          {accessorials.map((a) => (
            <SelectItem key={a.name} value={a.name}>
              {a.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  const renderRateType = ({
    accessorialRate,
    index,
    isNew = false,
    hasError = false,
  }) => {
    const onValueChange = (value) => {
      if (isNew) {
        handleNewAccessorialRateChange(
          ACCESSORIAL_RATE_FIELDS.RATE_TYPE,
          value,
        );
      } else {
        handleAccessorialRateFieldChange(
          value,
          index,
          ACCESSORIAL_RATE_FIELDS.RATE_TYPE,
        );
      }
    };

    return (
      <Select
        value={accessorialRate.ratetype || ''}
        onValueChange={onValueChange}
      >
        <SelectTrigger className={cn('w-full', hasError && ERROR_FIELD_CLASS)}>
          <SelectValue placeholder={t('DOLLAR_PERCENTAGE')} />
        </SelectTrigger>
        <SelectContent>
          {FORMATS.map((f) => (
            <SelectItem key={f.value} value={f.value}>
              {t(f.localizationSlug)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  const renderUnit = ({
    accessorialRate,
    index,
    isNew = false,
    hasError = false,
  }) => {
    const onValueChange = (value) => {
      if (isNew) {
        handleNewAccessorialRateChange(ACCESSORIAL_RATE_FIELDS.UNIT, value);
      } else {
        handleAccessorialRateFieldChange(
          value,
          index,
          ACCESSORIAL_RATE_FIELDS.UNIT,
        );
      }
    };

    return (
      <Select value={accessorialRate.units || ''} onValueChange={onValueChange}>
        <SelectTrigger className={cn('w-full', hasError && ERROR_FIELD_CLASS)}>
          <SelectValue placeholder={startCase(t('UNIT', { count: 1 }))} />
        </SelectTrigger>
        <SelectContent>
          {UNITS.map((u) => (
            <SelectItem key={u.value} value={u.value}>
              {u.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  const renderRate = ({
    accessorialRate,
    index,
    isNew = false,
    hasError = false,
  }) => {
    const onChange = (e) => {
      const value = e.target.value;
      if (isNew) {
        handleNewAccessorialRateChange(ACCESSORIAL_RATE_FIELDS.RATE, value);
      } else {
        handleAccessorialRateFieldChange(
          value,
          index,
          ACCESSORIAL_RATE_FIELDS.RATE,
        );
      }
    };

    return (
      <Input
        type="number"
        value={accessorialRate.rate ?? ''}
        onChange={onChange}
        placeholder="0"
        className={cn(hasError && ERROR_FIELD_CLASS)}
      />
    );
  };

  const renderQuoteId = ({ accessorialRate, index, isNew = false }) => {
    const onChange = (e) => {
      const value = e.target.value;
      if (isNew) {
        handleNewAccessorialRateChange(ACCESSORIAL_RATE_FIELDS.QUOTE_ID, value);
      } else {
        handleAccessorialRateFieldChange(
          value,
          index,
          ACCESSORIAL_RATE_FIELDS.QUOTE_ID,
        );
      }
    };

    return (
      <Input
        type="text"
        value={accessorialRate.quoteId ?? ''}
        onChange={onChange}
      />
    );
  };

  const renderNotes = ({ accessorialRate, index, isNew = false }) => {
    const onChange = (e) => {
      const value = e.target.value;
      if (isNew) {
        handleNewAccessorialRateChange(ACCESSORIAL_RATE_FIELDS.NOTES, value);
      } else {
        handleAccessorialRateFieldChange(
          value,
          index,
          ACCESSORIAL_RATE_FIELDS.NOTES,
        );
      }
    };

    return (
      <Input
        type="text"
        value={accessorialRate.notes ?? ''}
        onChange={onChange}
      />
    );
  };

  const renderLegType = ({ accessorialRate, index, isNew = false }) => {
    const onValueChange = (value) => {
      if (isNew) {
        handleNewAccessorialRateChange(ACCESSORIAL_RATE_FIELDS.LEG_TYPE, value);
      } else {
        handleAccessorialRateFieldChange(
          value,
          index,
          ACCESSORIAL_RATE_FIELDS.LEG_TYPE,
        );
      }
    };

    return (
      <Select
        value={accessorialRate.legType || ''}
        onValueChange={onValueChange}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={startCase(t('LEG_TYPE'))} />
        </SelectTrigger>
        <SelectContent>
          {LEG_TYPES.map((lt) => (
            <SelectItem key={lt.value} value={lt.value}>
              {lt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  const renderCheckbox = ({ accessorialRate, index, isNew = false }) => {
    const onCheckedChange = (value) => {
      if (isNew) {
        handleNewAccessorialRateChange(
          ACCESSORIAL_RATE_FIELDS.AUTO_APPLY,
          value,
        );
      } else {
        handleAccessorialRateFieldChange(
          value,
          index,
          ACCESSORIAL_RATE_FIELDS.AUTO_APPLY,
        );
      }
    };

    return (
      <Checkbox
        className="cursor-pointer"
        id={`auto-apply-checkbox-${index}`}
        defaultChecked={false}
        onCheckedChange={onCheckedChange}
        checked={accessorialRate?.autoApply || false}
      />
    );
  };

  const renderCustomerFileInfo = (customer) => {
    const uploadedName = customer.files?.[0]?.name;
    if (!uploadedName) return null;
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-green-700 font-medium">
        <CheckCircle className="h-3.5 w-3.5" />
        <Paperclip className="h-3 w-3" />
        {uploadedName}
      </span>
    );
  };

  // --- Section: Customers & Files ---
  const renderCustomersSection = () => (
    <section className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">
          {t('CUSTOMERS_AND_FILES', { defaultValue: 'Customers & Files' })}
        </h4>
        <p className="text-xs text-muted-foreground mt-1">
          {t('ACCESSORIAL_RATES_DOC_HINT', {
            defaultValue:
              'Upload a signed rate-sheet (e.g. contract addendum or pricing agreement) confirming these changes. A new file is required on every update.',
          })}
        </p>
      </div>
      {showErrors && !doCustomersHaveFiles && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t('CUSTOMERS_MISSING_REQUIRED_FILES_ERROR', {
              defaultValue:
                'Each customer must have a supporting document uploaded.',
            })}
          </AlertDescription>
        </Alert>
      )}
      <Table className="w-auto">
        <TableHeader>
          <TableRow>
            <TableHead>{startCase(t('USER', { count: 2 }))}</TableHead>
            <TableHead>{startCase(t('TYPE'))}</TableHead>
            <TableHead>{startCase(t('UPLOAD'))}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customerData.map((customer, i) => (
            <TableRow key={`customer-data-${i}`}>
              <TableCell>{customer.name}</TableCell>
              <TableCell>
                {customer.customerType === 'carrier' ? (
                  <Badge
                    variant="secondary"
                    className="bg-blue-100 text-blue-800 border-blue-300"
                  >
                    {startCase(t('CARRIER'))}
                  </Badge>
                ) : (
                  <Badge
                    variant="secondary"
                    className="bg-green-100 text-green-800 border-green-300"
                  >
                    {startCase(t('SHIPPER'))}
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-1 items-start">
                  <CargomaticDropzone
                    dataTestId={`CargomaticDropzone-${i}`}
                    onDrop={handleDropFiles(i)}
                    style={{ padding: 0, borderWidth: 0, borderRadius: 0 }}
                    multiple={false}
                    button
                    accept={ACCEPTED_FILE_TYPES}
                    disabled={isLoading}
                    dropzoneMessage=""
                    showIcon
                    color="green"
                  />
                  {renderCustomerFileInfo(customer)}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );

  // --- Section: Accessorial Rates ---
  const renderAccessorialRatesSection = () => (
    <section className="space-y-3">
      <h4 className="text-sm font-semibold text-gray-900">
        {startCase(t('ACCESSORIAL_RATES', { count: 2 }))}
      </h4>
      {/* Errors */}
      <div className="space-y-2 mb-4">
        {showErrors && !hasRates && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('ACCESSORIAL_RATE_REQUIRED', {
                defaultValue:
                  'Add at least one accessorial rate before submitting.',
              })}
            </AlertDescription>
          </Alert>
        )}
        {showErrors && !areDatesValid && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('INVALID_END_DATE', {
                defaultValue:
                  'Validity date range is invalid. End date must be after start date.',
              })}
            </AlertDescription>
          </Alert>
        )}
        {showErrors && duplicateAccessorial && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('ACCESSORIAL_RATE_EXIST', {
                defaultValue:
                  'Duplicate accessorial types are not allowed. Remove or change one.',
              })}
            </AlertDescription>
          </Alert>
        )}
        {showErrors && hasRates && !allRowsValid && !duplicateAccessorial && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('MISSING_REQUIRED_FIELDS', {
                defaultValue:
                  'Please fill in all required fields on highlighted rows.',
              })}
            </AlertDescription>
          </Alert>
        )}
        {deleteError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('ACCESSORIAL_RATE_REMOVE_FAILED', {
                defaultValue:
                  'Could not remove the accessorial rate. Please try again.',
              })}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Validity Dates */}
      <div className="mb-4">
        <Label className="mb-2">Validity Date Range</Label>
        <div className="flex flex-col md:flex-row md:items-center md:gap-3 gap-2 max-w-md">
          <Button
            variant="outline"
            className="h-auto justify-start gap-2 px-3 py-2 font-normal"
            onClick={() => handleAccessorialRateAppointment(0)}
          >
            <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="text-left">
              <div className="text-xs text-muted-foreground">
                {startCase(t('START'))}
              </div>
              <div className="text-sm">{formatDate(date.effectiveStart)}</div>
            </div>
          </Button>
          <Button
            variant="outline"
            className="h-auto justify-start gap-2 px-3 py-2 font-normal"
            onClick={() => handleAccessorialRateAppointment(0)}
          >
            <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="text-left">
              <div className="text-xs text-muted-foreground">
                {startCase(t('END'))}
              </div>
              <div className="text-sm">{formatDate(date.effectiveEnd)}</div>
            </div>
          </Button>
        </div>
        <AppointmentWindowModal
          show={
            showAccessorialRatesAppointmentModal.show &&
            showAccessorialRatesAppointmentModal.index === 0
          }
          onClose={hideAccessorialRateAppointment}
          validity={date}
          onStartChange={(start) => {
            setDate((prev) => ({
              ...prev,
              effectiveStart: start ? start.toISOString() : null,
            }));
          }}
          onEndChange={(end) => {
            setDate((prev) => ({
              ...prev,
              effectiveEnd: end ? end.toISOString() : null,
            }));
          }}
        />
      </div>

      {/* Rates Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">
                {t('ACCESSORIAL_TYPE')}
              </TableHead>
              <TableHead className="whitespace-nowrap">
                {t('ACCESSORIAL_CODE')}
              </TableHead>
              <TableHead className="whitespace-nowrap">
                {t('DOLLAR_PERCENTAGE')}
              </TableHead>
              <TableHead className="whitespace-nowrap">
                {startCase(t('RATE', { count: 1 }))}
              </TableHead>
              <TableHead className="whitespace-nowrap">
                {startCase(t('UNIT', { count: 1 }))}
              </TableHead>
              <TableHead className="whitespace-nowrap">
                {startCase(t('LEG_TYPE'))}
              </TableHead>
              {showCheckbox && (
                <TableHead className="whitespace-nowrap">
                  <TooltipProvider>
                    <span className="inline-flex items-center gap-1">
                      {startCase(t('AUTO_APPLY'))}?
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{t('AUTO_APPLY_NOTE')}</p>
                        </TooltipContent>
                      </Tooltip>
                    </span>
                  </TooltipProvider>
                </TableHead>
              )}
              <TableHead className="whitespace-nowrap">
                {t('QUOTE_ID')}
              </TableHead>
              <TableHead className="whitespace-nowrap">{t('NOTES')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {laneAccessorialRates.map((accessorialRate, index) => {
              const errors = rowErrors[index] || {};
              const rowShowErrors = showErrors;
              return (
                <TableRow key={`accessorial-rates-${index}`}>
                  <TableCell>
                    {accessorialRate.createdAt
                      ? accessorialRate.accessorialtype
                      : renderAccessorialRateType({
                          accessorialRate,
                          index,
                          hasError:
                            rowShowErrors &&
                            (errors.accessorialtype || errors.duplicate),
                        })}
                  </TableCell>
                  <TableCell>{accessorialRate.accesscode}</TableCell>
                  <TableCell>
                    {renderRateType({
                      accessorialRate,
                      index,
                      hasError: rowShowErrors && errors.ratetype,
                    })}
                  </TableCell>
                  <TableCell>
                    {renderRate({
                      accessorialRate,
                      index,
                      hasError: rowShowErrors && errors.rate,
                    })}
                  </TableCell>
                  <TableCell>
                    {renderUnit({
                      accessorialRate,
                      index,
                      hasError: rowShowErrors && errors.units,
                    })}
                  </TableCell>
                  <TableCell>
                    {renderLegType({ accessorialRate, index })}
                  </TableCell>
                  {showCheckbox && (
                    <TableCell>
                      {renderCheckbox({ accessorialRate, index })}
                    </TableCell>
                  )}
                  <TableCell>
                    {renderQuoteId({ accessorialRate, index })}
                  </TableCell>
                  <TableCell>
                    {renderNotes({ accessorialRate, index })}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      disabled={isLoading}
                      onClick={() => handleDeleteAccessorialRate(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">{t('DELETE')}</span>
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell>
                {renderAccessorialRateType({
                  accessorialRate: newAccessorialRate,
                  isNew: true,
                })}
              </TableCell>
              <TableCell>{newAccessorialRate.accesscode}</TableCell>
              <TableCell>
                {renderRateType({
                  accessorialRate: newAccessorialRate,
                  isNew: true,
                })}
              </TableCell>
              <TableCell>
                {renderRate({
                  accessorialRate: newAccessorialRate,
                  isNew: true,
                })}
              </TableCell>
              <TableCell>
                {renderUnit({
                  accessorialRate: newAccessorialRate,
                  isNew: true,
                })}
              </TableCell>
              <TableCell>
                {renderLegType({
                  accessorialRate: newAccessorialRate,
                  isNew: true,
                })}
              </TableCell>
              {showCheckbox && (
                <TableCell>
                  {renderCheckbox({
                    accessorialRate: newAccessorialRate,
                    isNew: true,
                  })}
                </TableCell>
              )}
              <TableCell>
                {renderQuoteId({
                  accessorialRate: newAccessorialRate,
                  isNew: true,
                })}
              </TableCell>
              <TableCell>
                {renderNotes({
                  accessorialRate: newAccessorialRate,
                  isNew: true,
                })}
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={isLoading}
                  onClick={() => handleAddNewAccessorialRate()}
                >
                  <Plus className="h-4 w-4" />
                  <span className="sr-only">{t('ADD')}</span>
                </Button>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </section>
  );

  // --- Main body ---
  const renderLoadingSkeleton = () => {
    const columnCount = showCheckbox ? 9 : 8;
    return (
      <div
        className="space-y-4"
        aria-busy="true"
        data-testid="accessorials-loading-skeleton"
      >
        <Skeleton className="h-5 w-64" />
        <div>
          <Skeleton className="h-4 w-20 mb-2" />
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-24" />
        </div>
        <div className="mb-4">
          <Skeleton className="h-4 w-36 mb-2" />
          <div className="flex flex-col md:flex-row md:gap-3 gap-2 max-w-md">
            <Skeleton className="h-12 md:w-40 w-full" />
            <Skeleton className="h-12 md:w-40 w-full" />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              {Array.from({ length: columnCount }).map((_, i) => (
                <TableHead key={i}>
                  <Skeleton className="h-4 w-20" />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 3 }).map((_, rowIdx) => (
              <TableRow key={rowIdx}>
                {Array.from({ length: columnCount }).map((_, colIdx) => (
                  <TableCell key={colIdx}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderBody = () => {
    if (isFetchingAccessorialRates) {
      return renderLoadingSkeleton();
    }

    return (
      <div className="space-y-4">
        {isLoading && (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-xs text-muted-foreground">
              {t('LOADING')}
            </span>
          </div>
        )}

        <div>
          <Label className="text-sm font-medium text-muted-foreground">
            {startCase(t('MARKET', { count: 1 }))}
          </Label>
          <p className="text-sm">{market}</p>
        </div>

        {renderCustomersSection()}
        {renderAccessorialRatesSection()}
      </div>
    );
  };

  const modalTitle = isEdit
    ? t('UPDATE_ACCESSORIAL_RATES', {
        defaultValue: 'Update Accessorial Rates',
      })
    : t('STEP_2_ENTER_ACCESSORIAL_RATE');

  return (
    <Dialog open={showModal} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[90vw] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{modalTitle}</DialogTitle>
          <DialogDescription>
            {t('ACCESSORIAL_RATES_MODAL_DESCRIPTION', {
              defaultValue:
                'Configure accessorial rates for this lane. A signed rate-sheet document is required for every update.',
            })}
          </DialogDescription>
        </DialogHeader>
        {renderBody()}
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onBack}
            disabled={loadingConfirmation || uploadingFiles}
          >
            {startCase(t('BACK'))}
          </Button>
          <Button
            onClick={handleClickSubmit}
            disabled={disableButtons || isLoading}
          >
            {(loadingConfirmation || uploadingFiles) && (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            )}
            {startCase(t('SUBMIT'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
